import axios from "axios";
import { useEffect, useState } from "react";
import { MdAccountBox } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function MyAccount() {
  const [form, setForm] = useState({
    accountNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    country: '',
    state: 'NY',
    borough: '',
    zip: '',
    address: '',
    city: '',
    license_number: '',
    license_plate: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    payment_method: '',
    center_name: '',
    rc_id: '',
  });

  const [centers, setCenters] = useState([]);
  const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });
  const [loading, setLoading] = useState(false);

  const [tenantServiceAreas, setTenantServiceAreas] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableBoroughs, setAvailableBoroughs] = useState([]);
  const [availableZips, setAvailableZips] = useState([]);
  const [noServiceArea, setNoServiceArea] = useState(false);


  // ✅ Fetch centers and driver data on load
  useEffect(() => {
    const token = sessionStorage.getItem("token");

    const fetchDriverData = async () => {
      try {
        const res = await axios.get("/auth/account", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = res.data.user;
        setForm((prev) => ({
          ...prev,
          accountNumber: user.accountNumber || '',
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          phone: user.phone || '',
          dob: user.dob ? new Date(user.dob).toISOString().slice(0, 10) : '',
          country: user.country || '',
          state: user.state || 'NY',
          borough: user.borough || '',
          zip: user.zip || '',
          address: user.address || '',
          city: user.city || '',
          license_number: user.license_number || '',
          license_plate: user.license_plate || '',
          vehicle_brand: user.vehicle_brand || '',
          vehicle_model: user.vehicle_model || '',
          vehicle_year: user.vehicle_year?.toString() || '',
          vehicle_color: user.vehicle_color || '',
          payment_method: user.payment_method || '',
          center_name: user.center_name || '',
          rc_id: user.rc_id || '',
        }));
        const tenantId = user.tenant_id;

        if (tenantId) {
          fetchCenters(tenantId);
          fetchServiceAreas(tenantId);
        }

      } catch (err) {
        console.error("Error fetching driver data:", err);
      }
    };

    const fetchCenters = async (tenant_id) => {
      try {
        const res = await axios.get(
          `/auth/redemption-centers/edit?tenant_id=${tenant_id}`
        );
        setCenters(res.data);
      } catch (err) {
        console.error("Failed to fetch centers:", err);
      }
    };


    fetchDriverData();
    fetchCenters();
  }, []);

  const fetchServiceAreas = async (tenant_id) => {
    try {
      const res = await axios.get(
        `/auth/tenant/service-area/${tenant_id}`
      );

      if (!res.data || res.data.length === 0) {
        setNoServiceArea(true);
        setTenantServiceAreas([]);
        setAvailableStates([]);
        setAvailableBoroughs([]);
        setAvailableZips([]);
        return;
      }

      setNoServiceArea(false);
      setTenantServiceAreas(res.data);

      const states = [...new Set(res.data.map(a => a.state))];
      setAvailableStates(states);
    } catch (err) {
      console.error("Failed to fetch service areas", err);
      setNoServiceArea(true);
    }
  };
  useEffect(() => {
    if (!tenantServiceAreas.length || !form.state) return;

    const boroughs = tenantServiceAreas
      .filter(a => a.state === form.state)
      .map(a => a.borough);

    setAvailableBoroughs([...new Set(boroughs)]);

    if (form.borough) {
      const zips = tenantServiceAreas
        .filter(
          a =>
            a.state === form.state &&
            a.borough === form.borough
        )
        .map(a => a.zipcode);

      setAvailableZips([...new Set(zips)]);
    }
  }, [tenantServiceAreas, form.state, form.borough]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => {
      let updated = { ...prev, [name]: value };

      if (name === "state") {
        const boroughs = tenantServiceAreas
          .filter(a => a.state === value)
          .map(a => a.borough);

        setAvailableBoroughs([...new Set(boroughs)]);
        setAvailableZips([]);
        updated.borough = "";
        updated.zip = "";
      }

      if (name === "borough") {
        const zips = tenantServiceAreas
          .filter(a => a.state === prev.state && a.borough === value)
          .map(a => a.zipcode);

        setAvailableZips([...new Set(zips)]);
        updated.zip = "";
      }

      if (name === "center_name") {
        const selectedCenter = centers.find(c => c.center_name === value);
        updated.rc_id = selectedCenter ? selectedCenter.rc_id : "";
      }

      return updated;
    });
  };


  const handleSubmit = async () => {
    const token = sessionStorage.getItem("token");
    setLoading(true);
    try {
      await axios.put("/auth/driveraccount", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Changes saved successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to save changes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white max-w-4xl mx-auto p-6 sm:p-8 w-98  px-2 sm:px-4 md:px-6 py-8 rounded-2xl shadow-lg ">
        <h1 className="text-3xl font-bold text-center text-green-700 mb-8 flex items-center justify-center gap-2">
          <MdAccountBox className="text-3xl" />
          My Account
        </h1>
        <form className="space-y-6">
          <div>
            <label className="block mb-1 font-medium">Driver ID</label>
            <input type="text" value={form.accountNumber} readOnly className="w-full p-3 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">First Name *</label>
              <input type="text" name="firstName" value={form.firstName} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" required />
            </div>
            <div>
              <label className="block mb-1 font-medium">Last Name *</label>
              <input type="text" name="lastName" value={form.lastName} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" required />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" required />
            </div>
            <div>
              <label className="block mb-1 font-medium">Phone Number *</label>
              <input type="text" name="phone" value={form.phone} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" required />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Date of Birth</label>
              <input type="date" name="dob" value={form.dob} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
            <div>
              <label className="block mb-1 font-medium">Country</label>
              <select name="country" value={form.country} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50">
                <option value="">Select Country</option>
                <option>Romania</option>
                <option>USA</option>
                <option>India</option>
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Center Name</label>
              <select name="center_name" value={form.center_name} onChange={handleChange}
                className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50">
                <option value="">Select Center</option>
                {centers.map((center) => (
                  <option key={center.id} value={center.center_name}>
                    {center.center_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">RC ID</label>
              <input type="text" name="rc_id" value={form.rc_id} readOnly
                className="w-full p-3 rounded-md bg-gray-100 border border-gray-300" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Street Address</label>
              <input type="text" name="address" value={form.address} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
            <div>
              <label className="block mb-1 font-medium">City</label>
              <input name="city" value={form.city} onChange={handleChange}
                type="text" placeholder="City" required
                className="w-full p-3 bg-lime-50 rounded-md border border-gray-300" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 font-medium">State</label>
              <select name="state" value={form.state} onChange={handleChange} required
                className="w-full p-3 bg-lime-50 rounded-md border border-gray-300">
                <option value="">Select State</option>
                {availableStates.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Borough</label>
              <select name="borough" value={form.borough} onChange={handleChange}
                disabled={!form.state} required
                className="w-full p-3 bg-lime-50 rounded-md border border-gray-300">
                <option value="">Select Borough</option>
                {availableBoroughs.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Zip</label>
              <select name="zip" value={form.zip} onChange={handleChange}
                disabled={!form.borough} required
                className="w-full p-3 bg-lime-50 rounded-md border border-gray-300">
                <option value="">Select ZIP</option>
                {availableZips.map(z => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select></div>
          </div>
          {noServiceArea && (
            <div className="p-4 rounded-md bg-red-50 border border-red-300 text-red-700 text-sm font-medium">
              🚫 This tenant has no active service areas. Please choose another tenant or try again later.
            </div>
          )}


          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Driver License Number</label>
              <input type="text" name="license_number" value={form.license_number} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
            <div>
              <label className="block mb-1 font-medium">License Plate Number</label>
              <input type="text" name="license_plate" value={form.license_plate} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Vehicle Brand</label>
              <input type="text" name="vehicle_brand" value={form.vehicle_brand} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
            <div>
              <label className="block mb-1 font-medium">Vehicle Model</label>
              <input type="text" name="vehicle_model" value={form.vehicle_model} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1 font-medium">Vehicle Year</label>
              <input type="text" name="vehicle_year" value={form.vehicle_year} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
            <div>
              <label className="block mb-1 font-medium">Vehicle Color</label>
              <input type="text" name="vehicle_color" value={form.vehicle_color} onChange={handleChange} className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50" />
            </div>
          </div>
          <div>
            <label className="block mb-1 font-medium">Payment Method</label>
            <select name="payment_method" value={form.payment_method} onChange={handleChange} required className="w-full rounded-lg p-3 ring-1 ring-gray-300 bg-lime-50">
              <option value="">Select Payment Method</option>
              <option value="Cash">Cash</option>
              <option value="ACH">ACH "Electronic Payment"</option>
              <option value="Ev-voucher">EV charging voucher"</option>
              <option value="Fuel-Voucher">Fuel Voucher</option>
              <option value="Stock-credit">Stock-credit</option>
              <option value="PayPal">PayPal</option>
              <option value="Check by Mail">Check by Mail</option>
            </select>
          </div>
          {/* Payment Email or Mobile */}
          {form.payment_method === 'PayPal' && (
            <div>
              <label className="block text-sm font-medium mb-1">PayPal Email</label>
              <input
                type="email"
                name="paymentContact"
                value={form.paymentContact}
                onChange={handleChange}
                placeholder="PayPal Email"
                className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                required
              />
            </div>
          )}

          {form.payment_method === 'ACH' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ACH Account Name</label>
                  <input
                    type="text"
                    name="ach_account_name"
                    value={form.ach_account_name || ''}
                    onChange={handleChange}
                    className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ACH Account Number</label>
                  <input
                    type="text"
                    name="ach_account_number"
                    value={form.ach_account_number || ''}
                    onChange={handleChange}
                    className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ACH Routing Number</label>
                  <input
                    type="text"
                    name="ach_routing_number"
                    value={form.ach_routing_number || ''}
                    onChange={handleChange}
                    className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ACH Account Type</label>
                  <select
                    name="ach_account_type"
                    value={form.ach_account_type || ''}
                    onChange={handleChange}
                    className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                    required
                  >
                    <option value="">Select Account Type</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stripe Account ID</label>
                <input
                  type="text"
                  name="stripe_account_id"
                  value={form.stripe_account_id || ''}
                  onChange={handleChange}
                  className="w-full p-2.5 rounded-md bg-lime-50 border text-sm"
                  required
                />

              </div>
            </>
          )}
          <div className="flex justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>

          </div>
        </form>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </section>
  );
}
